import { useQueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useParked } from '@web/features/parked/useParked';
import { useSession } from '@web/hooks/useSession';
import { useTheme } from '@web/hooks/useTheme';
import { signOut } from '@web/lib/auth';
import { cn } from '@web/lib/cn';
import { keys } from '@web/lib/keys';
import { getConnection, socket, subscribeConnection } from '@web/lib/socket';

/**
 * The console's frame.
 *
 * Desktop is a top rail; a phone gets bottom tabs with PARKED FIRST, because on
 * a phone the operator is almost always answering the question "is anyone
 * waiting?" and the queue is the answer. The thread route hides the tabs
 * entirely — a tab bar sitting behind the on-screen keyboard is a dead strip of
 * screen at exactly the moment screen is scarcest.
 */
const TABS = [
  { to: '/parked', label: 'Parked' },
  { to: '/board', label: 'Board' },
  { to: '/settings', label: 'Settings' },
];

function useConnection(): string {
  return useSyncExternalStore(subscribeConnection, getConnection, getConnection);
}

export function AppShell() {
  const location = useLocation();
  const onThread = location.pathname.startsWith('/thread/');

  return (
    /*
     * `--app-h` is set by `useVisualViewport` on the thread screen and falls
     * back to `100dvh` everywhere else. Driving the SHELL's height from it —
     * rather than the composer's position — is what keeps the composer above
     * the iOS keyboard: Safari resizes only the visual viewport, so a fixed
     * element would sit behind it.
     */
    <div
      className="grid grid-rows-[auto_1fr_auto] bg-ground"
      style={{ height: 'var(--app-h, 100dvh)' }}
    >
      <TopRail />
      <main className="min-h-0 overflow-hidden">
        <Outlet />
      </main>
      {!onThread && <TabBar />}
    </div>
  );
}

function TopRail() {
  const session = useSession();
  const parked = useParked();
  const connection = useConnection();
  const { theme, toggle } = useTheme();
  const client = useQueryClient();
  const navigate = useNavigate();
  const waiting = parked.data?.length ?? 0;

  return (
    <header className="flex items-stretch border-b border-rule bg-panel">
      <div className="flex items-center gap-2 border-r border-rule px-3">
        <span className="font-mono text-[11px] font-semibold tracking-[0.16em]">ADEL</span>
      </div>

      <nav className="hidden items-stretch md:flex" aria-label="Main">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2 border-r border-rule px-3 font-mono text-[11px] tracking-[0.1em] uppercase',
                isActive
                  ? 'bg-hover text-ink shadow-[inset_0_-2px_0_0_var(--ink)]'
                  : 'text-ink-faint hover:text-ink-dim',
              )
            }
          >
            {tab.label}
            {tab.to === '/parked' && waiting > 0 && (
              <span className="border border-signal px-1 text-[10px] text-signal tabular-nums">
                {waiting}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="ml-auto flex items-center gap-3 px-3">
        <ConnectionMark state={connection} />
        <button
          type="button"
          onClick={toggle}
          className="font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase hover:text-ink"
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        >
          {theme === 'dark' ? 'LIGHT' : 'DARK'}
        </button>
        <span className="hidden font-mono text-[11px] text-ink-dim sm:inline">
          {session.data?.name ?? ''}
        </span>
        <button
          type="button"
          className="font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase hover:text-ink"
          onClick={() => {
            void (async () => {
              await signOut();
              socket.disconnect();
              client.setQueryData(keys.me, null);
              client.clear();
              void navigate('/login', { replace: true });
            })();
          }}
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

/**
 * Connection state, encoded in shape as well as colour: a filled square is
 * live, a hollow one is trying, a barred one is off. Staff act on stale data
 * when a dead socket looks the same as a healthy one.
 */
function ConnectionMark({ state }: { state: string }) {
  const label =
    state === 'live'
      ? 'Live'
      : state === 'connecting'
        ? 'Reconnecting'
        : 'Offline — data may be stale';

  return (
    <span className="flex items-center gap-1.5" title={label}>
      <span
        aria-hidden
        className={cn(
          'inline-block size-[7px] border',
          state === 'live' && 'border-ok bg-ok',
          state === 'connecting' && 'border-warm bg-transparent',
          (state === 'offline' || state === 'idle') && 'border-signal bg-signal/30',
        )}
      />
      <span className="sr-only" role="status">
        {label}
      </span>
      <span
        aria-hidden
        className={cn(
          'hidden font-mono text-[10px] tracking-[0.1em] uppercase lg:inline',
          state === 'live' ? 'text-ink-faint' : 'text-warm',
        )}
      >
        {state === 'live' ? 'LIVE' : state === 'connecting' ? 'RECONNECTING' : 'OFFLINE'}
      </span>
    </span>
  );
}

function TabBar() {
  const parked = useParked();
  const waiting = parked.data?.length ?? 0;

  return (
    <nav
      aria-label="Sections"
      className="flex border-t border-rule bg-panel pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      {TABS.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          className={({ isActive }) =>
            cn(
              'flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 border-r border-rule py-2 last:border-r-0',
              isActive ? 'bg-hover text-ink' : 'text-ink-faint',
            )
          }
        >
          <span className="font-mono text-[11px] tracking-[0.1em] uppercase">{tab.label}</span>
          {tab.to === '/parked' && waiting > 0 && (
            <span className="border border-signal px-1 font-mono text-[10px] text-signal tabular-nums">
              {waiting}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
